import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { real_id } from 'src/helpers/interface_helpers';
import { devices } from 'src/stores/lists/devices';
import { isBluLiquidLevelSensor } from 'src/helpers/blu_liquid_level_sensor/blu_liquid_level_sensor_helpers';
import { getCameraZoneName } from '../../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
import { extractCameraObjectsParams } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-objects-helpers';
import { getCameraObjectLabel } from 'src/components/Camera/helpers/camera-objects';
import { VENTORA_FAN_SOURCE_I18N } from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';
import type { SceneConditionTransform } from 'src/types/ScenesConditionsTypes';

let _format;
let _f = format.subscribe((e) => (_format = e));

export const ID_TO_I18N_CONDITIONS_EQUAL = (
	id,
	value,
	gen,
	translated?: {
		input_index?: number;
		add_info_ind?: string | number;
		device?: { id?: string };
		transform?: SceneConditionTransform;
	},
) => {
	switch (id) {
		case 'bolt_state':
			return `${_format('scene_condition_bolt_state')} ${_format(`condition_operator_bolt_state_${value}`)}`;
		case 'fan_on':
			return _format('when_device_state_msg', {
				values: { state: _format(`when_turn_${value}`) },
			});
		case 'fan_source': {
			const sourceI18nKey = VENTORA_FAN_SOURCE_I18N[value];
			const sourceLabel = sourceI18nKey ? _format(sourceI18nKey, { default: value }) : value;
			return `${_format('scene_condition_fan_source')} ${sourceLabel}`;
		}
		case 'turn':
		case 'state':
			return _format('when_device_state_msg', {
				values: { state: _format(`when_turn_${value}`) },
			});

		case 'roller state':
		case 'multi_cover_state':
			if (gen == 2) {
				return _format('when_device_state_msg1', {
					values: { state: _format(`on_${value}_state`) },
				});
			} else {
				return _format('when_roller_state_msg', {
					values: { state: _format(`when_${value}`) },
				});
			}
		case 'roller direction':
			return _format(`when_last_state_msg`, {
				values: { state: _format(`when_was_${value}`) },
			});

		case 'input_value':
		case 'digital_input_add_on': {
			const key =
				translated?.input_index !== undefined
					? _format('collapse_title_input_state_by_channel', {
							values: { n: translated.input_index + 1 },
						})
					: _format('prop_input_turn');
			return _format(`scene_condition_base`, {
				values: { key, value: _format(value) },
			});
		}

		case 'push_type':
		case 'push_type_add_on':
			return _format('scene_condition_button_push', {
				values: { push: _format(`prop_${value}`) },
			});

		case 'door':
		case 'window':
			return _format('scene_condition_door', {
				values: { door_state: _format(`door_${value}`) },
			});

		case 'luxury':
			return _format('scene_condition_illumination', {
				values: { value: _format(value) },
			});

		case 'ext_switch':
			return _format(`when_external_switch_${value}`);
		case 'valve_open_state':
			return _format('scene_condition_valve_state', {
				values: { state: _format(`state_${value}`) },
			});
		case 'reported_by':
			return _format('when_ble_device_reported_by', {
				default: "Device's state reported by ",
			});
		case 'remote_control_channel':
			return _format('when_remote_control_channel', {
				values: { channel: _format(`channel_${value}`) },
				default: `When remote control channel is`,
			});
		case 'scroll_position':
			return _format('when_scroll_position', {
				values: { scroll: _format(`scroll_${value}`) },
				default: `When scroll position is`,
			});
		case 'tariff':
			return _format('scene_condition_base', {
				values: { key: _format('prop_tariff', { default: 'tariff' }), value },
			});
		case 'camera_motion':
			return value === 'true'
				? _format('when_camera_motion_detected', { default: 'When camera motion is detected' })
				: _format('when_camera_no_motion', { default: 'When camera has no motion' });
		case 'camera_privacy':
			return value === 'true'
				? _format('when_camera_privacy_enabled', { default: 'When privacy mode is enabled' })
				: _format('when_camera_privacy_disabled', { default: 'When privacy mode is disabled' });
		case 'camera_arm':
			return value === 'true'
				? _format('when_camera_armed', { default: 'When camera is armed' })
				: _format('when_camera_disarmed', { default: 'When camera is disarmed' });
		case 'camera_objects': {
			const { type, nobjs, minscore } = extractCameraObjectsParams(translated?.transform);
			const object = getCameraObjectLabel(type);
			return _format('when_camera_objects_detected', {
				values: { count: nobjs, object, confidence: minscore },
				default: `When the camera detects ${nobjs} ${object} with at least ${minscore}% confidence`,
			});
		}
		case 'camera_zone_motion': {
			const zoneName = translated?.add_info_ind
				? getCameraZoneName(translated.add_info_ind, readDeviceSettings(translated?.device?.id))
				: _format('camera_zone', { default: 'zone' });
			return value === 'true'
				? _format('when_camera_zone_motion_detected', {
						values: { zone: zoneName },
						default: `When motion is detected in ${zoneName}`,
					})
				: _format('when_camera_zone_motion_stopped', {
						values: { zone: zoneName },
						default: `When motion stops in ${zoneName}`,
					});
		}
		case 'moisture_alarm': {
			const deviceId = translated?.device?.id;
			const deviceType =
				get(devices)?.[deviceId]?.type || get(devices)?.[real_id(deviceId)]?.type;
			const isLiquidLevel = isBluLiquidLevelSensor(deviceType);
			return _format('when_sensor_detects_msg', {
				values: {
					detects: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
					sensor_name: _format(isLiquidLevel ? 'prop_liquid_level' : 'prop_moisture_alarm', {
						default: isLiquidLevel ? 'the liquid level' : 'the rain status',
					}),
				},
			});
		}
		default:
			return _format('when_sensor_detects_msg', {
				values: {
					detects: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
					sensor_name: _format(id),
				},
			});
	}
};

export const ID_TO_I18N_PROP = (id, gen) => {
	let propName = '';
	switch (id) {
		case 'roller position':
		case 'multi_cover_position':
			if (gen == 2) return _format('prop_device_pos');
			propName = _format('prop_roller_position');
			break;
		case 'total_current':
		case 'meter':
			propName = _format('prop_power_meter');
			break;
		case 'dist':
			propName = _format('prop_distance');
			break;
		case 'moisture':
			propName = _format('prop_moisture');
			break;
		default:
			propName = _format(`prop_${id}`);
			break;
	}
	return propName;
};
